import AntButton from "antd/es/button";
import styled, { css } from "styled-components";
import { disabledStyle } from "common/Buttons/styles";
import { hexToRGB } from "@shared/utils/misc";
import type { ComponentType } from "react";
import { phoneMaxWidth, phoneMinWidth } from "common/NewLook/layout.ts";

export const buttonStyles = {
  antBtn: css<{
    $type?: ButtonType;
  }>`
    background: ${(p) => p.theme.colors.cardBgColor};
    color: ${(p) => p.theme.colors.textColor};
    border-color: ${(p) => p.theme.colors.mediumBorderGrey};

    &:not(:disabled):not(.ant-btn-disabled):hover {
      background: ${(p) => p.theme.colors.cardBgColor};
      color: ${(p) => p.theme.colors.textColor};
      border-color: ${(p) => p.theme.colors.mediumBorderGrey};
    }

    ${(p) =>
      p.$type === "secondary" &&
      `
      &:hover {
        background: ${(p) => p.theme.colors.cardBgColor} !important;
        color: ${(p) => p.theme.colors.primaryBlue} !important;
        border-color: ${(p) => p.theme.colors.primaryBlue} !important;
        ${(p) =>
          p.$type !== "outlined" &&
          `
          @media (max-width: ${phoneMaxWidth(p)}) {
            background: ${p.theme.colors.cardBgColor} !important;
            color: ${p.theme.colors.textColor} !important;
            border-color: ${p.theme.colors.mediumBorderGrey} !important;
          }
        `}
      }
    `}
  `,
  antBtnPrimary: css`
    background: ${(p) => p.theme.colors.primaryBlue} !important;
    border-color: ${(p) => p.theme.colors.primaryBlue} !important;
    color: ${(p) => p.theme.colors.buttonText} !important;

    &:hover {
      background: ${(p) =>
        hexToRGB(p.theme.colors.primaryBlue, 0.85)} !important;
      border-color: ${(p) => p.theme.colors.primaryBlue} !important;
      color: ${(p) => p.theme.colors.buttonText} !important;
    }
  `,
  antBtnDefault: css`
    &:not(:disabled):not(.ant-btn-disabled):hover {
      @media (min-width: ${phoneMinWidth}) {
        color: ${(p) => p.theme.colors.primaryBlue};
        border-color: ${(p) => p.theme.colors.primaryBlue};
        background: inherit;
      }
    }
  `,
  antBtnDangerous: css`
    border-color: ${(p) => p.theme.colors.red} !important;
    color: ${(p) => p.theme.colors.red} !important;
    &.ant-btn-primary {
      background: ${(p) => p.theme.colors.cardBgColor} !important;
    }

    &:hover {
      border-color: ${(p) => p.theme.colors.red} !important;
      color: ${(p) => hexToRGB(p.theme.colors.red, 0.7)} !important;
    }
  `,
  antBtnDisabled: css`
    opacity: 0.4;

    &:hover {
      color: ${(p) => p.theme.colors.buttonText};
      @media (min-width: ${phoneMinWidth}) {
        border-color: ${(p) => p.theme.colors.mediumBorderGrey};
        color: ${(p) => p.theme.colors.textColor};
      }
    }
  `,
};

type ButtonType = "secondary" | "outlined";

const Button = styled(AntButton)<{
  $size?: "l";
  $error?: boolean;
  $type?: ButtonType;
  $disabled?: boolean;
}>`
  &.ant-btn.ant-btn-loading::before {
    display: none;
  }

  &.ant-btn {
    ${buttonStyles.antBtn};
  }

  &.ant-btn-primary {
    ${buttonStyles.antBtnPrimary};
  }

  &.ant-btn-link {
    border: none;
    color: ${(p) => p.theme.colors.primaryBlue} !important;
    background-color: inherit;
  }

  &.ant-btn-default {
    ${buttonStyles.antBtnDefault};
  }

  &.ant-btn-dangerous {
    ${buttonStyles.antBtnDangerous};
  }

  &.ant-btn[disabled] {
    ${buttonStyles.antBtnDisabled};
  }

  ${(p) =>
    p.$size === "l" &&
    `
    height: 40px;
    font-size: ${p.theme.typography.fontSizes.L};
  `}

  ${(p) => p.$error && disabledStyle}
  ${(p) =>
    p.$error &&
    `
    &:hover {
        border-color: ${p.theme.colors.mediumBorderGrey} !important;
        color: ${p.theme.colors.textColor} !important;
    }
  `}
  ${(p) =>
    p.$type === "secondary" &&
    `
    background: ${hexToRGB(p.theme.colors.primaryBlue, 0.12)} !important;
    color: ${p.theme.colors.primaryBlue} !important;
    border: none !important;
  `}
  ${(p) =>
    p.$type === "outlined" &&
    `
    font-weight: 500;
    background: transparent !important;
    color: ${p.theme.colors.primaryBlue} !important;
    border-color: ${p.theme.colors.primaryBlue} !important;
    &:hover {
      border-color: ${p.theme.colors.primaryBlue};
    }
  `}

  ${(p) =>
    p.$disabled &&
    `
      ${buttonStyles.antBtnDisabled};
  `}
`;

export const StylableButton = Button as ComponentType<Record<string, unknown>>;

export default Button;

export const StyledDefaultButton = styled(Button)`
  &.ant-btn-default {
    background: ${(p) => hexToRGB(p.theme.colors.primaryBlue, 0.1)} !important;
    color: ${(p) => p.theme.colors.primaryBlue} !important;
    border: none;
    box-shadow: none;
  }

  &.ant-btn-default:not(:disabled):not(.ant-btn-disabled):hover {
    @media (min-width: ${phoneMinWidth}) {
      background: ${(p) =>
        hexToRGB(p.theme.colors.primaryBlue, 0.15)} !important;
    }
  }

  &.ant-btn > .ant-btn-loading-icon .anticon svg {
    color: ${(p) => p.theme.colors.primaryBlue};
  }
`;
